/**
 * SAMPLE USAGE
 * -> INJECT AlertQueueService
 * 
 * let alert = await this.alertCtrl.create({
      header: 'QUEUE ALERT',
      buttons: [{
        text: 'Cancel',
        handler: () => {}
      }, {
        text: 'Ok',
        handler: () => {}
      }]
    });

    alert.onDidDismiss().then((value) => {});

    this.alertQueue.queueAlert(alert, HubAlertModule.GEN_ERR, false);
 */

import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AlertController } from '@ionic/angular';
import { AlertOptions } from '@ionic/core';
import { HubAlertModule } from './alert-queue.module.constant';

import size from 'lodash/size';
import filter from 'lodash/filter';
import each from 'lodash/each';

export interface HubAlertObject {
  alert: HTMLIonAlertElement;
  module: HubAlertModule;
}

@Injectable({
  providedIn: 'root',
})
export class AlertQueueService {
  private _currentAlert: HubAlertObject;

  private _alerts: HubAlertObject[] = [];

  public newNotifChanged: Subject<HubAlertObject> = new Subject<HubAlertObject>();

  public delayForNextCheck: number = 500; // in milliseconds

  constructor(public alertCtrl: AlertController) {}

  /**
   * Creates an alert element and insert to the queue
   * @param alert - The created alert object through AlertController
   * @param urgent
   */
  async createAndQueue(
    opts: AlertOptions,
    module: HubAlertModule,
    canDismissAllSameModule: boolean = false,
    urgent: boolean = false,
  ): Promise<HubAlertObject> {
    // Always clone the opts to prevent overwriting
    // opts = cloneDeep(opts);

    if (canDismissAllSameModule && this._countMultipleModulesExist(module) > 0) {
      opts.buttons.unshift({
        text: 'Dismiss All',
        role: 'dismiss',
        handler: data => {
          // remove all with the same modules
          this._alerts = filter(this._alerts, (alert: HubAlertObject) => alert.module != module);
        },
      });
    }

    // create the alert object
    let alert = await this.alertCtrl.create(opts);
    if (alert) {
      let newAlert = { alert: alert, module: module };
      if (urgent) {
        this._alerts.unshift(newAlert);
      } else {
        this._alerts.push(newAlert);
      }

      if (!this._currentAlert) {
        this._checkDisplayedAlert();
      }

      return newAlert;
    }

    return null;
  }

  private _countMultipleModulesExist(module: HubAlertModule): number {
    let moduleCount: number = 0;
    if (this._currentAlert && this._currentAlert.module == module) {
      moduleCount++;
    }

    moduleCount += size(filter(this._alerts, (alert: HubAlertObject) => alert.module == module));
    return moduleCount;
  }

  /**
   * Queues list of alerts to show
   * @param alert - The created alert object through AlertController
   * @param urgent - Moves the alert to front of the list
   */
  public queueAlert(alert: any, module: HubAlertModule = HubAlertModule.GENERAL_ALERT, urgent: boolean = false): void {
    let newAlert = { alert: alert, module: module };
    if (urgent) {
      this._alerts.unshift(newAlert);
    } else {
      this._alerts.push(newAlert);
    }

    if (!this._currentAlert) {
      this._checkDisplayedAlert();
    }
  }

  public clearByModule(module: HubAlertModule): void {
    this._alerts = filter(this._alerts, (alert: HubAlertObject) => alert.module != module);
  }

  public clear(): void {
    if (this._currentAlert && this._currentAlert.alert) {
      this._currentAlert.alert.dismiss();
    }

    this._alerts = [];
  }

  private _alertClosed = () => {
    this._currentAlert = null;

    setTimeout(() => {
      this._checkDisplayedAlert();
    }, this.delayForNextCheck);
  };

  async _checkDisplayedAlert() {
    if (size(this._alerts)) {
      if (!this._currentAlert) {
        this._currentAlert = this._alerts.shift();
        await this._currentAlert.alert.present();

        this._currentAlert.alert.onDidDismiss().then(value => {
          this._alertClosed();
        });
      }
    }
  }
}
